import React, { useState, useEffect, Fragment, useMemo } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import { api } from '../config/api';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronUpDownIcon,
  DocumentMagnifyingGlassIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  PhotoIcon,
  XCircleIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

// react-pdf worker setup. Matches the config in Dhyana.tsx so both pages
// share the same worker asset.
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PendingBook {
  id: string;
  title: string;
  author: string;
  category?: string;
  isbn?: string;
  description?: string;
  status: string;
  uploaded_at: string;
  file_size?: number;
  total_pages?: number;
  cover_presigned_url?: string;
  file_presigned_url?: string;
  uploader_email: string;
  uploader_name?: string;
  duplicate_of_title?: string;
  duplicate_of_id?: string;
  // Institute attribution — present when the upload was tagged to an
  // institute (i.e. uploaded from a tenant subdomain by an institute member).
  institution_id?: string;
  institution_name?: string;
  institution_slug?: string;
}

interface ApprovedBook {
  id: string;
  title: string;
  author: string;
}

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: 'text-amber-700', bg: 'bg-amber-100' },
  in_review: { label: 'In Review', color: 'text-blue-700', bg: 'bg-blue-100' },
  approved: { label: 'Approved', color: 'text-green-700', bg: 'bg-green-100' },
  rejected: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-100' },
};

const REJECTION_REASONS = [
  { key: 'ocr_quality', label: 'OCR / scan quality too low' },
  { key: 'copyright', label: 'Copyrighted content we cannot host' },
  { key: 'duplicate', label: 'Duplicate of a book we already have' },
  { key: 'off_topic', label: 'Not a textbook / off-topic' },
  { key: 'other', label: 'Other (explain below)' },
];

const MIN_REJECTION_LENGTH = 20;

// Small inline component — renders a thumbnail for a newly-selected File via
// URL.createObjectURL, or falls back to the existing CDN cover URL. Handles
// lifecycle so the blob URL gets revoked when the File changes or unmounts.
const CoverPreview: React.FC<{
  file?: File;
  existingUrl?: string;
  isDarkMode: boolean;
}> = ({ file, existingUrl, isDarkMode }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const src = objectUrl || existingUrl;
  const frame = `h-20 w-14 rounded-md flex-shrink-0 overflow-hidden border ${
    isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-gray-200 bg-gray-100'
  }`;

  if (!src) {
    return (
      <div className={`${frame} flex items-center justify-center`}>
        <PhotoIcon className={`h-6 w-6 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
      </div>
    );
  }
  return <img src={src} alt="Cover preview" className={`${frame} object-cover`} />;
};

const AdminBookApprovals: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { showToast } = useToast();
  const [books, setBooks] = useState<PendingBook[]>([]);
  const [approvedBooks, setApprovedBooks] = useState<ApprovedBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingMeta, setEditingMeta] = useState<Record<string, any>>({});
  const [coverFiles, setCoverFiles] = useState<Record<string, File>>({});
  // Per-book rejection state — separate from metadata so a half-typed rejection
  // reason does not leak into the metadata save path.
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReasonKey, setRejectionReasonKey] = useState<string>('ocr_quality');
  const [rejectionText, setRejectionText] = useState<string>('');
  // Per-book link candidate state (Combobox input + selection).
  const [linkQuery, setLinkQuery] = useState<Record<string, string>>({});
  const [linkCandidate, setLinkCandidate] = useState<Record<string, ApprovedBook | null>>({});
  // Global action-in-flight flag keyed by book id.
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Link-candidate fetch is lazy — we only hit /api/books when the admin opens
  // the first combobox. `approvedBooksLoaded` guards against repeated fetches.
  const [approvedBooksLoaded, setApprovedBooksLoaded] = useState(false);
  // Known categories for the datalist on the Category field.
  const [categories, setCategories] = useState<string[]>([]);
  // PDF preview modal — inline instead of opening a new tab, so admin keeps
  // the approval card in context.
  const [previewBook, setPreviewBook] = useState<PendingBook | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewNumPages, setPreviewNumPages] = useState<number>(0);
  const [previewPage, setPreviewPage] = useState<number>(1);

  useEffect(() => {
    fetchPendingBooks();
    fetchCategories();
  }, []);

  const fetchPendingBooks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/books/admin/pending');
      setBooks(response.data);
    } catch (error) {
      console.error('Failed to fetch pending books:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedBooks = async () => {
    if (approvedBooksLoaded) return;
    try {
      const response = await api.get('/api/books/', { params: { limit: 200 } });
      const approved = response.data
        .filter((b: any) => b.status === 'approved')
        .map((b: any) => ({ id: b.id, title: b.title, author: b.author }));
      setApprovedBooks(approved);
      setApprovedBooksLoaded(true);
    } catch (error) {
      console.error('Failed to fetch approved books:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/api/books/categories');
      // Strip the sentinel 'all' entry that the library filter uses.
      setCategories(response.data.filter((c: string) => c !== 'all'));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const saveMetadata = async (bookId: string): Promise<boolean> => {
    const meta = editingMeta[bookId] || {};
    const formData = new FormData();
    ['title', 'author', 'category', 'isbn', 'description'].forEach((key) => {
      if (meta[key] !== undefined && meta[key] !== null) {
        formData.append(key, meta[key]);
      }
    });
    const coverFile = coverFiles[bookId];
    if (coverFile) {
      formData.append('cover_image', coverFile);
    }
    try {
      await api.put(`/api/books/admin/${bookId}/metadata`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return true;
    } catch (error: any) {
      showToast('error', 'Save failed', error.response?.data?.detail || 'Could not save metadata');
      return false;
    }
  };

  const handleSaveMetadata = async (bookId: string) => {
    setActionLoading(bookId);
    const ok = await saveMetadata(bookId);
    setActionLoading(null);
    if (ok) {
      showToast('success', 'Metadata saved');
      setCoverFiles((prev) => { const next = { ...prev }; delete next[bookId]; return next; });
      fetchPendingBooks();
    }
  };

  const handleApprove = async (bookId: string) => {
    setActionLoading(bookId);
    // Save any edited metadata first so admin doesn't lose pending edits on approve.
    const ok = await saveMetadata(bookId);
    if (!ok) {
      setActionLoading(null);
      return;
    }
    try {
      await api.post(`/api/books/admin/${bookId}/review`, { action: 'approve' });
      showToast('success', 'Book approved');
      setExpandedId(null);
      fetchPendingBooks();
    } catch (error: any) {
      showToast('error', 'Approve failed', error.response?.data?.detail || 'Could not approve');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartReject = (bookId: string) => {
    setRejectingId(bookId);
    setRejectionReasonKey('ocr_quality');
    setRejectionText('');
  };

  const handleCancelReject = () => {
    setRejectingId(null);
    setRejectionText('');
  };

  const handleConfirmReject = async (bookId: string) => {
    const reasonLabel = REJECTION_REASONS.find((r) => r.key === rejectionReasonKey)?.label || '';
    const needsCustomText = rejectionReasonKey === 'other';
    const composed = needsCustomText
      ? rejectionText.trim()
      : `${reasonLabel}${rejectionText.trim() ? ` — ${rejectionText.trim()}` : ''}`;

    if (needsCustomText && composed.length < MIN_REJECTION_LENGTH) {
      showToast(
        'error',
        'Rejection reason too short',
        `Please provide at least ${MIN_REJECTION_LENGTH} characters explaining the rejection.`,
      );
      return;
    }

    setActionLoading(bookId);
    try {
      await api.post(`/api/books/admin/${bookId}/review`, {
        action: 'reject',
        rejection_reason: composed,
      });
      showToast('success', 'Book rejected');
      setRejectingId(null);
      setRejectionText('');
      setExpandedId(null);
      fetchPendingBooks();
    } catch (error: any) {
      showToast('error', 'Reject failed', error.response?.data?.detail || 'Could not reject');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLinkAndApprove = async (bookId: string) => {
    const target = linkCandidate[bookId];
    if (!target) {
      showToast('error', 'Select a target book first');
      return;
    }
    // Confirmation guards against accidental approves on dropdown selection.
    if (!window.confirm(`Link this submission to "${target.title}" and approve? The uploader will read the shared edition.`)) {
      return;
    }
    setActionLoading(bookId);
    try {
      await api.post(`/api/books/admin/${bookId}/review`, {
        action: 'link_existing',
        existing_book_id: target.id,
      });
      showToast('success', 'Linked & approved');
      setExpandedId(null);
      setLinkCandidate((prev) => { const next = { ...prev }; delete next[bookId]; return next; });
      setLinkQuery((prev) => { const next = { ...prev }; delete next[bookId]; return next; });
      fetchPendingBooks();
    } catch (error: any) {
      showToast('error', 'Link failed', error.response?.data?.detail || 'Could not link');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMoveToInReview = async (bookId: string) => {
    setActionLoading(bookId);
    try {
      await api.post(`/api/books/admin/${bookId}/review`, { action: 'in_review' });
      showToast('success', 'Moved to In Review');
      fetchPendingBooks();
    } catch (error: any) {
      showToast('error', 'Update failed', error.response?.data?.detail || 'Could not update');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePreviewPdf = async (book: PendingBook) => {
    try {
      const response = await api.get(`/api/books/${book.id}/read-url`);
      setPreviewBook(book);
      setPreviewUrl(response.data.url);
      setPreviewPage(1);
      setPreviewNumPages(0);
    } catch (error: any) {
      showToast('error', 'Failed to generate preview URL');
    }
  };

  const closePreview = () => {
    setPreviewBook(null);
    setPreviewUrl(null);
    setPreviewNumPages(0);
    setPreviewPage(1);
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return null;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredBooks = statusFilter === 'all'
    ? books
    : books.filter((b) => b.status === statusFilter);

  const toggleExpand = (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      handleCancelReject();
    } else {
      setExpandedId(id);
      const book = books.find((b) => b.id === id);
      if (book) {
        setEditingMeta((prev) => ({
          ...prev,
          [id]: {
            title: book.title,
            author: book.author,
            category: book.category || '',
            isbn: book.isbn || '',
            description: book.description || '',
          },
        }));
      }
    }
  };

  const inputClass = `w-full px-3 py-1.5 text-sm rounded-lg border ${
    isDarkMode
      ? 'bg-slate-800 border-slate-600 text-white'
      : 'bg-white border-gray-300 text-gray-900'
  } focus:outline-none focus:ring-1 focus:ring-cyan-500`;

  const labelClass = `block text-xs font-medium mb-1 capitalize ${
    isDarkMode ? 'text-slate-400' : 'text-gray-500'
  }`;

  return (
    <Layout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Book Approvals
          </h1>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>
            Review and approve user-submitted books
          </p>
        </div>

        {/* Status filter tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: 'all', label: `All (${books.length})` },
            { key: 'pending', label: `Pending (${books.filter((b) => b.status === 'pending').length})` },
            { key: 'in_review', label: `In Review (${books.filter((b) => b.status === 'in_review').length})` },
            { key: 'approved', label: `Approved (${books.filter((b) => b.status === 'approved').length})` },
            { key: 'rejected', label: `Rejected (${books.filter((b) => b.status === 'rejected').length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                statusFilter === tab.key
                  ? 'bg-cyan-500 text-white'
                  : isDarkMode
                    ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-cyan-400' : 'text-cyan-500'}`} />
          </div>
        ) : filteredBooks.length === 0 ? (
          <div className="text-center py-12">
            <CheckIcon className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {statusFilter === 'all' ? 'No books in the queue' : `No ${statusFilter.replace('_', ' ')} books`}
            </h3>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBooks.map((book) => {
              const isExpanded = expandedId === book.id;
              const statusInfo = STATUS_LABELS[book.status] || STATUS_LABELS.pending;
              const meta = editingMeta[book.id] || {};
              const isLoading = actionLoading === book.id;
              const isRejecting = rejectingId === book.id;
              const isTerminal = book.status === 'approved' || book.status === 'rejected';
              const displayTitle = book.title?.trim() || 'Uploaded file (no title yet)';

              // Filter approved-books list by the current query for this card.
              const query = linkQuery[book.id] || '';
              const filteredCandidates = query.trim() === ''
                ? approvedBooks
                : approvedBooks.filter((ab) =>
                    `${ab.title} ${ab.author || ''}`.toLowerCase().includes(query.toLowerCase()),
                  );

              return (
                <div
                  key={book.id}
                  // No overflow-hidden here: the link-candidate Combobox is
                  // absolutely-positioned and would get clipped at the card
                  // bottom otherwise (visible as a half-cut dropdown that
                  // disappears under the next pending-book card).
                  className={`rounded-xl border ${
                    isDarkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Card header — shows title + author + uploader context */}
                  <div
                    className={`flex items-center justify-between p-4 cursor-pointer ${
                      isDarkMode ? 'hover:bg-slate-800/50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleExpand(book.id)}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <p className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {displayTitle}
                        </p>
                        {book.author && (
                          <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                            by {book.author}
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusInfo.bg} ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                        {/* Institute chip — surfaces when Book.institution_id
                            is set, so the platform admin can prioritize
                            institute-curated uploads in the queue. The
                            backend also sorts these to the top. */}
                        {book.institution_name && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                            isDarkMode ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-50 text-cyan-700'
                          }`}>
                            <BuildingLibraryIcon className="h-3 w-3" />
                            {book.institution_name}
                          </span>
                        )}
                        {book.duplicate_of_title && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap bg-yellow-100 text-yellow-800">
                            <ExclamationTriangleIcon className="h-3 w-3" />
                            Duplicate?
                          </span>
                        )}
                      </div>
                      <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        <span className="whitespace-nowrap">{book.uploader_email}</span>
                        <span className="opacity-40">·</span>
                        <span className="whitespace-nowrap">{formatDate(book.uploaded_at)}</span>
                        {formatFileSize(book.file_size) && (
                          <>
                            <span className="opacity-40">·</span>
                            <span className="whitespace-nowrap">{formatFileSize(book.file_size)}</span>
                          </>
                        )}
                        {book.total_pages && (
                          <>
                            <span className="opacity-40">·</span>
                            <span className="whitespace-nowrap">{book.total_pages} pages</span>
                          </>
                        )}
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronUpIcon className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                      : <ChevronDownIcon className={`h-5 w-5 flex-shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                    }
                  </div>

                  {/* Expanded form */}
                  {isExpanded && (
                    <div className={`border-t p-4 space-y-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      {/* Duplicate warning */}
                      {book.duplicate_of_title && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                          Possible duplicate of <strong>{book.duplicate_of_title}</strong>. Consider linking instead.
                        </div>
                      )}

                      {/* Metadata fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(['title', 'author', 'category', 'isbn'] as const).map((field) => (
                          <div key={field}>
                            <label className={labelClass}>{field}</label>
                            <input
                              type="text"
                              value={meta[field] || ''}
                              onChange={(e) =>
                                setEditingMeta((prev) => ({
                                  ...prev,
                                  [book.id]: { ...prev[book.id], [field]: e.target.value },
                                }))
                              }
                              list={field === 'category' ? 'category-suggestions' : undefined}
                              className={inputClass}
                            />
                          </div>
                        ))}
                        <div className="sm:col-span-2">
                          <label className={labelClass}>Description</label>
                          <textarea
                            rows={2}
                            value={meta.description || ''}
                            onChange={(e) =>
                              setEditingMeta((prev) => ({
                                ...prev,
                                [book.id]: { ...prev[book.id], description: e.target.value },
                              }))
                            }
                            className={inputClass}
                          />
                        </div>
                      </div>

                      {/* Cover image — preview existing or newly-selected file */}
                      <div>
                        <label className={labelClass}>Cover Image</label>
                        <div className="flex items-start gap-4">
                          <CoverPreview
                            file={coverFiles[book.id]}
                            existingUrl={book.cover_presigned_url}
                            isDarkMode={isDarkMode}
                          />
                          <div className="flex-1">
                            <label className="cursor-pointer text-sm font-medium text-cyan-500 hover:text-cyan-400">
                              {coverFiles[book.id] ? 'Change image' : (book.cover_presigned_url ? 'Replace cover' : 'Choose cover image')}
                              <input
                                type="file"
                                className="sr-only"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) setCoverFiles((prev) => ({ ...prev, [book.id]: file }));
                                }}
                              />
                            </label>
                            {coverFiles[book.id] && (
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                {coverFiles[book.id].name} ({Math.round(coverFiles[book.id].size / 1024)} KB)
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Preview / Download — admin can flip through inline
                          OR pull the raw PDF down to inspect offline. The
                          presigned download URL is generated server-side so
                          the link expires shortly. */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handlePreviewPdf(book)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg ${
                            isDarkMode
                              ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <DocumentMagnifyingGlassIcon className="h-4 w-4" />
                          Preview file
                        </button>
                        {book.file_presigned_url && (
                          <a
                            href={book.file_presigned_url}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg ${
                              isDarkMode
                                ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                            Download
                          </a>
                        )}
                      </div>

                      {/* Link to Existing — searchable combobox. Picking a candidate
                          sets state but does NOT auto-approve; admin must click
                          "Link & Approve" explicitly. */}
                      {!isTerminal && (
                        <div>
                          <label className={labelClass}>Link to Existing Book (optional)</label>
                          <div className="flex gap-2 items-start">
                            <Combobox
                              value={linkCandidate[book.id] || null}
                              onChange={(value: ApprovedBook | null) =>
                                setLinkCandidate((prev) => ({ ...prev, [book.id]: value }))
                              }
                            >
                              <div className="relative flex-1">
                                <Combobox.Input
                                  className={inputClass}
                                  displayValue={(b: ApprovedBook | null) =>
                                    b ? `${b.title}${b.author ? ` — ${b.author}` : ''}` : ''
                                  }
                                  onChange={(e) =>
                                    setLinkQuery((prev) => ({ ...prev, [book.id]: e.target.value }))
                                  }
                                  onFocus={fetchApprovedBooks}
                                  placeholder={approvedBooksLoaded ? 'Search by title or author...' : 'Click to load candidates...'}
                                />
                                <Combobox.Button
                                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                                  onClick={fetchApprovedBooks}
                                >
                                  <ChevronUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                                </Combobox.Button>
                                <Transition
                                  as={Fragment}
                                  leave="transition ease-in duration-100"
                                  leaveFrom="opacity-100"
                                  leaveTo="opacity-0"
                                  afterLeave={() => setLinkQuery((prev) => ({ ...prev, [book.id]: '' }))}
                                >
                                  <Combobox.Options
                                    // Headless UI v2: `anchor` portals the options
                                    // to the document body and uses floating-ui
                                    // positioning so the dropdown stays inside the
                                    // viewport (auto-flips up if there's no room
                                    // below). `var(--input-width)` is the v2 CSS
                                    // var that mirrors the ComboboxInput width so
                                    // the dropdown stays the same width as the
                                    // trigger field.
                                    anchor={{ to: 'bottom start', gap: 4 }}
                                    className={`z-50 [width:var(--input-width)] max-h-60 overflow-auto rounded-lg py-1 text-sm shadow-lg ring-1 ring-black/5 ${
                                      isDarkMode ? 'bg-slate-800 text-white' : 'bg-white text-gray-900'
                                    }`}
                                  >
                                    {filteredCandidates.length === 0 ? (
                                      <div className={`px-3 py-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                        No matching books
                                      </div>
                                    ) : (
                                      filteredCandidates.map((ab) => (
                                        <Combobox.Option
                                          key={ab.id}
                                          value={ab}
                                          className={({ active }) =>
                                            `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                                              active
                                                ? 'bg-cyan-500 text-white'
                                                : isDarkMode ? 'text-slate-200' : 'text-gray-900'
                                            }`
                                          }
                                        >
                                          <div className="truncate font-medium">{ab.title}</div>
                                          {ab.author && (
                                            <div className="truncate text-xs opacity-75">by {ab.author}</div>
                                          )}
                                        </Combobox.Option>
                                      ))
                                    )}
                                  </Combobox.Options>
                                </Transition>
                              </div>
                            </Combobox>
                            <button
                              onClick={() => handleLinkAndApprove(book.id)}
                              disabled={isLoading || !linkCandidate[book.id]}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <LinkIcon className="h-4 w-4" />
                              Link & Approve
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Rejection panel — appears when admin clicks Reject, stays until confirmed or cancelled */}
                      {isRejecting && (
                        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-red-950/30 border-red-900' : 'bg-red-50 border-red-200'}`}>
                          <label className={labelClass}>Rejection reason</label>
                          <select
                            value={rejectionReasonKey}
                            onChange={(e) => setRejectionReasonKey(e.target.value)}
                            className={`${inputClass} mb-2`}
                          >
                            {REJECTION_REASONS.map((r) => (
                              <option key={r.key} value={r.key}>{r.label}</option>
                            ))}
                          </select>
                          <textarea
                            rows={2}
                            value={rejectionText}
                            onChange={(e) => setRejectionText(e.target.value)}
                            placeholder={
                              rejectionReasonKey === 'other'
                                ? `Explain why (min ${MIN_REJECTION_LENGTH} chars). The uploader sees this.`
                                : 'Optional extra detail. The uploader sees this.'
                            }
                            className={inputClass}
                          />
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => handleConfirmReject(book.id)}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                            >
                              {isLoading ? (
                                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                              ) : (
                                <XCircleIcon className="h-4 w-4" />
                              )}
                              Confirm Reject
                            </button>
                            <button
                              onClick={handleCancelReject}
                              disabled={isLoading}
                              className={`inline-flex items-center px-4 py-1.5 text-sm font-medium rounded-lg ${
                                isDarkMode
                                  ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Action buttons — explicit, one per action. No single "Save" that
                          silently changes state. Metadata save is separate from status changes. */}
                      {!isRejecting && (
                        <div className="flex flex-wrap gap-2 pt-2">
                          <button
                            onClick={() => handleSaveMetadata(book.id)}
                            disabled={isLoading}
                            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg ${
                              isDarkMode
                                ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            } disabled:opacity-50`}
                          >
                            {isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                            Save Metadata
                          </button>

                          {book.status === 'pending' && (
                            <button
                              onClick={() => handleMoveToInReview(book.id)}
                              disabled={isLoading}
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              In Review
                            </button>
                          )}

                          {!isTerminal && (
                            <>
                              <button
                                onClick={() => handleApprove(book.id)}
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                              >
                                {isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckCircleIcon className="h-4 w-4" />}
                                Approve
                              </button>
                              <button
                                onClick={() => handleStartReject(book.id)}
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                              >
                                <XCircleIcon className="h-4 w-4" />
                                Reject
                              </button>
                            </>
                          )}

                          {isTerminal && (
                            <span className={`self-center text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                              This book is {book.status}. Status cannot be changed — the user re-uploads to retry.
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Global datalist for Category — shared across every expanded card */}
      <datalist id="category-suggestions">
        {categories.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>

      {/* Inline PDF preview modal — uses react-pdf so admin can flip through
          pages without leaving the approval page. */}
      <Transition appear show={previewBook !== null} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closePreview}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full max-w-4xl h-[85vh] flex flex-col rounded-xl shadow-2xl overflow-hidden ${
                    isDarkMode ? 'bg-slate-900' : 'bg-white'
                  }`}
                >
                  <div className={`flex items-center justify-between px-5 py-3 border-b ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-200'
                  }`}>
                    <div className="min-w-0">
                      <Dialog.Title className={`text-sm font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        Preview — {previewBook?.title?.trim() || 'Uploaded file'}
                      </Dialog.Title>
                      <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {previewBook?.uploader_email} · {previewBook?.total_pages || previewNumPages || '?'} pages
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {previewNumPages > 0 && (
                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                          <button
                            onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                            disabled={previewPage <= 1}
                            className={`px-2 py-1 rounded ${
                              isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
                            } disabled:opacity-30`}
                          >
                            ‹
                          </button>
                          <span>{previewPage} / {previewNumPages}</span>
                          <button
                            onClick={() => setPreviewPage((p) => Math.min(previewNumPages, p + 1))}
                            disabled={previewPage >= previewNumPages}
                            className={`px-2 py-1 rounded ${
                              isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'
                            } disabled:opacity-30`}
                          >
                            ›
                          </button>
                        </div>
                      )}
                      <button
                        onClick={closePreview}
                        className={`p-1 rounded-lg ${
                          isDarkMode ? 'text-slate-400 hover:bg-slate-800' : 'text-gray-500 hover:bg-gray-100'
                        }`}
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className={`flex-1 overflow-auto flex items-start justify-center p-4 ${
                    isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
                  }`}>
                    {previewUrl && (
                      <Document
                        file={previewUrl}
                        onLoadSuccess={({ numPages }) => setPreviewNumPages(numPages)}
                        loading={
                          <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                            Loading preview…
                          </div>
                        }
                        error={
                          <div className="text-sm text-red-500">
                            Could not render this file. EPUB preview is not supported inline.
                          </div>
                        }
                      >
                        <Page
                          pageNumber={previewPage}
                          width={720}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      </Document>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Layout>
  );
};

export default AdminBookApprovals;
